import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaTimes, FaSync, FaExternalLinkAlt } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import { blobApiErrorMessage } from './documentHelpers';

export default function DocumentPreviewModal({ file, baseUrl, onClose }) {
    const { token } = useAuth();
    const [objectUrl, setObjectUrl] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!file) return undefined;
        let cancelled = false;
        let createdUrl = null;
        setObjectUrl(null);
        setError('');

        axios.get(`${baseUrl}/files/${encodeURIComponent(file.id)}/content?inline=1`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
        })
            .then((res) => {
                if (cancelled) return;
                createdUrl = window.URL.createObjectURL(new Blob([res.data], { type: file.mimeType }));
                setObjectUrl(createdUrl);
            })
            .catch(async (err) => {
                if (!cancelled) setError(await blobApiErrorMessage(err, 'Could not load the preview.'));
            });

        return () => {
            cancelled = true;
            if (createdUrl) window.URL.revokeObjectURL(createdUrl);
        };
    }, [file, baseUrl, token]);

    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (!file) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 md:p-6" onClick={onClose}>
            <div className="flex h-full max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-3">
                    <p className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800" title={file.name}>{file.name}</p>
                    {file.webViewLink && (
                        <a href={file.webViewLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#FF5733] hover:underline">
                            <FaExternalLinkAlt /> <span className="hidden md:inline">Open in Drive</span>
                        </a>
                    )}
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close preview">
                        <FaTimes />
                    </button>
                </div>
                <div className="flex flex-1 items-center justify-center overflow-auto bg-gray-50">
                    {error ? (
                        <p className="px-4 text-sm text-red-500">{error}</p>
                    ) : !objectUrl ? (
                        <p className="flex items-center gap-2 text-sm text-gray-400"><FaSync className="animate-spin" /> Loading preview…</p>
                    ) : file.mimeType === 'application/pdf' ? (
                        <iframe src={objectUrl} title={file.name} className="h-full w-full border-0" />
                    ) : (
                        <img src={objectUrl} alt={file.name} className="max-h-full max-w-full object-contain" />
                    )}
                </div>
            </div>
        </div>
    );
}
